import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Button, 
  CircularProgress, 
  IconButton, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Paper, 
  Tooltip, 
  Typography,
  Modal
} from '@mui/material';
import { 
  AttachFile, 
  Delete, 
  Download, 
  InsertDriveFile, 
  Image, 
  PictureAsPdf, 
  AudioFile, 
  VideoFile, 
  Visibility,
  Close
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import ConfirmDialog from '../common/ConfirmDialog';
import anexoService, { Anexo } from '../../services/anexoService';
import { useAuth } from '../../contexts/AuthContext';

// Função para formatar o tamanho do arquivo
const formatarTamanhoArquivo = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

interface AnexosTarefaProps {
  tarefaId: string;
  podeEditar: boolean;
}

const AnexosTarefa: React.FC<AnexosTarefaProps> = ({ tarefaId, podeEditar }) => {
  const [anexos, setAnexos] = useState<Anexo[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [previewNome, setPreviewNome] = useState<string>('');
  const { usuario } = useAuth();
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Função para obter o ícone correto com base no tipo de arquivo
  const getFileIcon = (tipo: string) => {
    if (tipo.startsWith('image/')) return <Image color="primary" />;
    if (tipo.startsWith('application/pdf')) return <PictureAsPdf color="error" />;
    if (tipo.startsWith('audio/')) return <AudioFile color="secondary" />;
    if (tipo.startsWith('video/')) return <VideoFile color="success" />;
    return <InsertDriveFile color="info" />;
  };

  // Função para carregar anexos
  const carregarAnexos = useCallback(async () => {
    if (!tarefaId) return;
    
    setCarregando(true);
    try {
      const response = await anexoService.listarAnexosTarefa(tarefaId);
      if (response.success) {
        setAnexos(response.anexos);
      } else {
        toast.error(response.message || 'Erro ao carregar anexos');
      }
    } catch (error) {
      console.error('Erro ao carregar anexos:', error);
      toast.error('Erro ao carregar anexos');
    } finally {
      setCarregando(false);
    }
  }, [tarefaId]);

  // Carregar anexos quando o componente montar
  useEffect(() => {
    carregarAnexos();
  }, [carregarAnexos]);

  // Função para fazer upload de arquivo
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validar tamanho do arquivo (50MB)
    if (file.size > 50 * 1024 * 1024) {
      toast.error('Arquivo muito grande. Tamanho máximo: 50MB');
      return;
    }

    setEnviando(true);
    try {
      const response = await anexoService.uploadAnexo(tarefaId, file);
      if (response.success) {
        toast.success('Arquivo enviado com sucesso!');
        carregarAnexos(); // Recarregar lista de anexos
      } else {
        toast.error(response.message || 'Erro ao enviar arquivo');
      }
    } catch (error) {
      console.error('Erro ao enviar arquivo:', error);
      toast.error('Erro ao enviar arquivo');
    } finally {
      setEnviando(false);
      // Limpar o input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Função para fazer download de anexo
  const handleDownloadAnexo = async (anexo: Anexo) => {
    try {
      toast.info('Iniciando download...', { autoClose: 2000 });

      const downloadUrl = await anexoService.downloadAnexo(anexo.id);

      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = anexo.nome_original || anexo.id;
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

    } catch (error) {
      console.error('Erro ao baixar anexo:', error);
      toast.error('Erro ao baixar arquivo');
    }
  };

  // Função para visualizar anexo (preview)
  const handlePreviewAnexo = async (anexo: Anexo) => {
    try {
      // Verificar se é uma imagem
      if (!anexo.tipo.startsWith('image/')) {
        toast.info('Preview disponível apenas para imagens');
        return;
      }

      toast.info('Carregando preview...', { autoClose: 2000 });
      
      const previewUrl = await anexoService.getPreviewUrl(anexo.id);
      
      setPreviewUrl(previewUrl);
      setPreviewNome(anexo.nome_original);
      setPreviewOpen(true);
      
    } catch (error) {
      console.error('Erro ao carregar preview:', error);
      toast.error('Erro ao carregar preview');
    }
  };

  // Função para excluir anexo — abre a confirmação em vez de excluir na hora
  const [anexoParaExcluir, setAnexoParaExcluir] = useState<string | null>(null);

  const handleExcluirAnexo = (anexoId: string) => {
    setAnexoParaExcluir(anexoId);
  };

  const confirmarExclusaoAnexo = async () => {
    if (!anexoParaExcluir) return;
    const anexoId = anexoParaExcluir;
    setAnexoParaExcluir(null);

    try {
      const response = await anexoService.excluirAnexo(anexoId);
      if (response.success) {
        toast.success('Anexo excluído com sucesso!');
        carregarAnexos(); // Recarregar lista de anexos
      } else {
        toast.error(response.message || 'Erro ao excluir anexo');
      }
    } catch (error) {
      console.error('Erro ao excluir anexo:', error);
      toast.error('Erro ao excluir anexo');
    }
  };

  // Verificar se o usuário pode excluir um anexo
  const podeExcluirAnexo = (anexoUsuarioId: string) => {
    return podeEditar || (usuario && usuario.id === anexoUsuarioId);
  };

  return (
    <>
      <Paper sx={{ p: 2, mt: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            Anexos
            {anexos.length > 0 && ` (${anexos.length})`}
          </Typography>
          
          {podeEditar && (
            <Box>
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileUpload}
                accept="*/*"
              />
              <Button
                variant="contained"
                startIcon={enviando ? <CircularProgress size={20} /> : <AttachFile />}
                onClick={() => fileInputRef.current?.click()}
                disabled={enviando}
                size="small"
              >
                {enviando ? 'Enviando...' : 'Adicionar Anexo'}
              </Button>
            </Box>
          )}
        </Box>

        {carregando ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : anexos.length === 0 ? (
          <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>
            Nenhum anexo encontrado
          </Typography>
        ) : (
          <List>
            {anexos.map(anexo => (
              <ListItem
                key={anexo.id}
                divider
                secondaryAction={
                  <Box display="flex" gap={1}>
                    {/* Botão de preview para imagens */}
                    {anexo.tipo.startsWith('image/') && (
                      <Tooltip title="Visualizar imagem">
                        <IconButton 
                          onClick={() => handlePreviewAnexo(anexo)}
                          color="info"
                          size="small"
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                    )}
                    
                    {/* Botão de download */}
                    <Tooltip title="Baixar anexo">
                      <IconButton 
                        onClick={() => handleDownloadAnexo(anexo)}
                        color="primary"
                        size="small"
                      >
                        <Download />
                      </IconButton>
                    </Tooltip>
                    
                    {/* Botão de excluir */}
                    {podeExcluirAnexo(anexo.usuario_id) && (
                      <Tooltip title="Excluir anexo">
                        <IconButton 
                          onClick={() => handleExcluirAnexo(anexo.id)}
                          color="error"
                          size="small"
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                }
              >
                <ListItemIcon>
                  {getFileIcon(anexo.tipo)}
                </ListItemIcon>
                <ListItemText
                  primary={anexo.nome_original}
                  secondary={
                    <>
                      <Typography variant="body2" component="span">
                        {formatarTamanhoArquivo(anexo.tamanho)}
                      </Typography>
                      <Typography variant="caption" component="span" sx={{ ml: 1 }}>
                        {new Date(anexo.data_upload).toLocaleDateString('pt-BR')}
                      </Typography>
                      {anexo.usuario_nome && (
                        <Typography variant="caption" component="span" sx={{ ml: 1 }}>
                          por {anexo.usuario_nome}
                        </Typography>
                      )}
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </Paper>
      
      {/* Modal de preview de imagens */}
      <Modal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        aria-labelledby="preview-modal-title"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            height: '90%',
            bgcolor: 'background.paper',
            boxShadow: 24,
            borderRadius: 1,
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {/* Cabeçalho do modal */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            p={2}
            borderBottom={1}
            borderColor="divider"
          >
            <Typography variant="h6" component="h2" noWrap>
              {previewNome}
            </Typography>
            <IconButton onClick={() => setPreviewOpen(false)}>
              <Close />
            </IconButton>
          </Box>

          {/* Conteúdo do modal */}
          <Box 
            flexGrow={1} 
            display="flex" 
            justifyContent="center" 
            alignItems="center"
            overflow="hidden"
            p={2}
          >
            {previewUrl && (
              <img 
                src={previewUrl} 
                alt={previewNome}
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain'
                }}
              />
            )}
          </Box>
        </Box>
      </Modal>

      <ConfirmDialog
        open={!!anexoParaExcluir}
        title="Excluir anexo"
        message="Tem certeza que deseja excluir este anexo?"
        confirmText="Excluir"
        onConfirm={confirmarExclusaoAnexo}
        onCancel={() => setAnexoParaExcluir(null)}
      />
    </>
  );
};

export default AnexosTarefa;
